import type { ComponentsDefinition } from '@eneco/cms';
import { cmsComponents } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

/**
 * The CMS-agnostic registry for this container: the shared one plus what only this
 * container mounts.
 *
 * Applications are registered per container on the Sitecore lane too, in
 * `sitecoreComponents.ts`, and for the same reason: they cannot live in the shared
 * registry. `lib:cms-components` may not depend on `app:next` (see `depConstraints`
 * in `eslint.config.mjs`), and all three containers pass that one map, so a flow put
 * there would also be pulled into the two containers that never render it.
 *
 * The cookie wall is one of those applications. It sits in the page's `jss-meta`
 * placeholder, which `CmsLayout` renders on this lane too, and reads its content
 * through `@eneco/cms`, so the same component serves both lanes. The chat sits there too; its CMS-lane entry
 * is `ChatInner`, which reads its content through `@eneco/cms`.
 *
 * The registration flow is registered under both its rendering names as its CMS-lane entry
 * (`RegistrationFlowInner`), which picks the variant from the rendering's name.
 *
 * The inspiration advice filter is one too, registered as its inner component: this lane's
 * `Placeholder` already hands it its rendering, which the Sitecore lane's bridge builds itself.
 *
 * `AppPlaceholder` looks the key up by the name after `jss-app-`, so it has to match
 * the placeholder on the page, and it matches the Sitecore rendering name one to one.
 *
 * A flow is added here once its steps read their content through `@eneco/cms`. Until
 * then it stays out: its steps still read Sitecore directly, so on this lane it would
 * fail at the first step rather than render.
 */
const components: ComponentsDefinition = {
  ...cmsComponents,
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat').then(mod => mod.ChatInner)),
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  InspirationAdviceFilter: dynamic(() =>
    import('@apps/inspirationadvicefilter-nl-eneco/InspirationAdviceFilter').then(
      mod => mod.InspirationAdviceFilterInner,
    ),
  ),
  'legislation-compass-flow': dynamic(
    () => import('@apps/flows-nl-eneco-legislationCompassFlow/LegislationCompassFlow'),
  ),
  RegistrationAccountCreate: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  RegistrationAccountReset: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
};

export default components;
